import { DestroyRef, Injectable, NgZone } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export const SESSION_EXPIRED_REASON = 'session-expired';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_KEY = 'user_info';
  // sessionStorage, because it has to survive the round trip through Cognito's hosted login.
  private readonly RETURN_URL_KEY = 'auth_return_url';
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(this.isTokenValid());
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;
  private sessionExpiryHandled = false;

  constructor(private router: Router, private zone: NgZone, destroyRef: DestroyRef) {
    // Check if user is authenticated on service initialization
    this.checkAuthStatus();
    this.scheduleExpiry();

    // Timers are throttled or frozen in background tabs and while the machine sleeps.
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        this.checkExpiry();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    destroyRef.onDestroy(() => {
      document.removeEventListener('visibilitychange', onVisible);
      this.cancelExpiry();
    });
  }

  /**
   * Check if the user is authenticated by validating the token
   */
  private checkAuthStatus(): void {
    const isAuthenticated = this.isTokenValid();
    this.isAuthenticatedSubject.next(isAuthenticated);
  }

  /**
   * Check if the token exists and is valid
   */
  public isTokenValid(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    // Check if token is expired
    try {
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      const expirationTime = tokenData.exp * 1000; // Convert to milliseconds
      return Date.now() < expirationTime;
    } catch (error) {
      console.error('Error parsing token:', error);
      return false;
    }
  }

  /**
   * Get the authentication token from local storage
   */
  public getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Get user information from local storage
   */
  public getUserInfo(): any {
    const userInfo = localStorage.getItem(this.USER_KEY);
    return userInfo ? JSON.parse(userInfo) : null;
  }

  /**
   * Save authentication token and user info to local storage
   */
  public setAuthData(token: string, userInfo: any): void {
    localStorage.setItem(this.TOKEN_KEY, token);
    localStorage.setItem(this.USER_KEY, JSON.stringify(userInfo));
    this.sessionExpiryHandled = false;
    this.isAuthenticatedSubject.next(true);
    this.scheduleExpiry();
  }

  /**
   * Clear authentication data from local storage
   */
  public clearAuthData(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    this.cancelExpiry();
    this.isAuthenticatedSubject.next(false);
  }

  /**
   * Redirect to Cognito login page
   */
  public login(): void {
    window.location.href = this.managedLoginUrl('oauth2/authorize');
  }

  /**
   * Redirect to managed login to register a passkey. Cognito only allows this for a user
   * who has already signed in at least once.
   */
  public addPasskey(): void {
    window.location.href = this.managedLoginUrl('passkeys/add');
  }

  private managedLoginUrl(path: string): string {
    const { domain, clientId, redirectUri } = environment.cognito;

    return `https://${domain}/${path}?client_id=${clientId}&response_type=token&scope=email+openid+profile&redirect_uri=${encodeURIComponent(redirectUri)}`;
  }

  /**
   * Handle the authentication callback from Cognito
   */
  public handleAuthCallback(): void {
    const hash = window.location.hash;
    if (hash) {
      const params = new URLSearchParams(hash.substring(1));
      const accessToken = params.get('access_token');
      const idToken = params.get('id_token');

      if (accessToken && idToken) {
        // Decode the ID token to get user information
        const userInfo = this.decodeToken(idToken);

        this.setAuthData(idToken, userInfo);

        this.router.navigateByUrl(this.consumeReturnUrl());
      } else {
        console.error('No tokens found in the URL hash');
        this.router.navigate(['/login']);
      }
    } else {
      console.error('No hash found in the URL');
      this.router.navigate(['/login']);
    }
  }

  /**
   * Decode a JWT token
   */
  private decodeToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = window.atob(base64);
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  /**
   * Sign out of both the app and Cognito. The Cognito session cookie outlives our local
   * state, so without this redirect the next sign-in re-issues tokens without a prompt.
   */
  public logout(): void {
    this.clearAuthData();
    window.location.href = this.cognitoLogoutUrl();
  }

  private cognitoLogoutUrl(): string {
    const { domain, clientId, logoutUri } = environment.cognito;

    return `https://${domain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
  }

  /**
   * End the local session because the token expired or was rejected, and send the user to
   * login with a session-expired notice. The Cognito session is left alone so a still-valid
   * one can re-issue tokens without the user re-entering credentials.
   */
  public handleSessionExpired(returnUrl: string = this.router.url): void {
    if (this.sessionExpiryHandled) {
      return;
    }
    this.sessionExpiryHandled = true;
    this.rememberReturnUrl(returnUrl);
    this.clearAuthData();
    this.router.navigate(['/login'], { queryParams: { reason: SESSION_EXPIRED_REASON } });
  }

  /**
   * Where a guard sends a user without a valid token. A stored but expired token means the
   * session ran out, so it is cleared and the login page says why.
   */
  public loginRedirect(returnUrl: string): UrlTree {
    this.rememberReturnUrl(returnUrl);
    if (this.getToken() === null) {
      return this.router.createUrlTree(['/login']);
    }
    this.sessionExpiryHandled = true;
    this.clearAuthData();
    return this.router.createUrlTree(['/login'], { queryParams: { reason: SESSION_EXPIRED_REASON } });
  }

  private checkExpiry(): void {
    if (this.getToken() !== null && !this.isTokenValid()) {
      this.handleSessionExpired();
    }
  }

  private scheduleExpiry(): void {
    this.cancelExpiry();
    const expiresAt = this.tokenExpiry();
    if (expiresAt === null) {
      return;
    }
    // Outside Angular so a day-long pending timer does not keep the app from becoming stable.
    this.zone.runOutsideAngular(() => {
      this.expiryTimer = setTimeout(
        () => this.zone.run(() => this.checkExpiry()),
        Math.max(expiresAt - Date.now(), 0)
      );
    });
  }

  private cancelExpiry(): void {
    if (this.expiryTimer !== null) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = null;
    }
  }

  private tokenExpiry(): number | null {
    const token = this.getToken();
    const exp = token ? this.decodeToken(token)?.exp : undefined;
    return typeof exp === 'number' ? exp * 1000 : null;
  }

  private rememberReturnUrl(url: string): void {
    if (this.isSafeReturnUrl(url)) {
      sessionStorage.setItem(this.RETURN_URL_KEY, url);
    }
  }

  private consumeReturnUrl(): string {
    const url = sessionStorage.getItem(this.RETURN_URL_KEY);
    sessionStorage.removeItem(this.RETURN_URL_KEY);
    return url && this.isSafeReturnUrl(url) ? url : '/';
  }

  /** Login and callback are excluded so signing in never lands back on them. */
  private isSafeReturnUrl(url: string): boolean {
    return url.startsWith('/') && !url.startsWith('//') && !/^\/(login|callback)\b/.test(url);
  }
}
